import queryString from 'query-string'

import { emptySplitApi } from 'emptySplitApi'
import { SessionId } from 'store/apiHeaders/types'
import { Area } from 'store/area/types'

import {
  Breakdown,
  CertificationType,
  DiversityMakeup,
  LevelCompletion,
  PayGapBreakdown,
  PracticeCriteriaType,
  Report,
  ReportGroup,
  ReportProjection,
  ReportType,
} from './types'

const reportsApi = emptySplitApi.injectEndpoints({
  endpoints: build => ({
    getReportAreas: build.query<Partial<Area>[], { sessionId: SessionId }>({
      query: ({ sessionId }) => ({
        url: `areas`,
        headers: {
          'fairhq-session': sessionId ? sessionId.toString() : '',
        },
      }),
    }),
    getCompanyBreakdown: build.query<
      Partial<Breakdown>[],
      { sessionId: SessionId }
    >({
      query: ({ sessionId }) => ({
        url: `companies/breakdown`,
        headers: {
          'fairhq-session': sessionId ? sessionId.toString() : '',
        },
      }),
    }),
    getCompanyPayGapBreakdown: build.query<
      Partial<PayGapBreakdown>[],
      { sessionId: SessionId }
    >({
      query: ({ sessionId }) => ({
        url: `companies/breakdown/paygap`,
        headers: {
          'fairhq-session': sessionId ? sessionId.toString() : '',
        },
      }),
    }),
    getReports: build.query<
      Partial<Report>,
      {
        type: ReportType
        sessionId: SessionId
      }
    >({
      query: ({ type, sessionId }) => ({
        url: `reports?${queryString.stringify({
          type,
        })}`,
        headers: {
          'fairhq-session': sessionId ? sessionId.toString() : '',
        },
      }),
    }),
    getReportByArea: build.query<
      Partial<Report>,
      {
        areaCode?: string
        projection?: ReportProjection
        group?: ReportGroup
        sessionId: SessionId
      }
    >({
      query: ({ areaCode = '', projection, group, sessionId }) => ({
        url: `reports/${areaCode}?${queryString.stringify({
          projection,
          group,
        })}`,
        headers: {
          'fairhq-session': sessionId ? sessionId.toString() : '',
        },
      }),
    }),
    getDiversityMakeupReports: build.query<
      DiversityMakeup[],
      {
        sessionId: SessionId
        breakdown?: boolean
      }
    >({
      query: ({ sessionId, breakdown }) => ({
        url: `reports/diversity/makeup?${queryString.stringify({
          breakdown,
        })}`,
        headers: {
          'fairhq-session': sessionId ? sessionId.toString() : '',
        },
      }),
    }),
    getPractices: build.query<
      PracticeCriteriaType[],
      { areaCode?: string; sessionId: SessionId }
    >({
      query: ({ areaCode, sessionId }) => ({
        url: `reports/practices?${
          areaCode
            ? queryString.stringify({
                areaCode,
              })
            : ''
        }`,
        headers: { 'fairhq-session': sessionId ? sessionId.toString() : '' },
      }),
      keepUnusedDataFor: 0,
    }),
    getCertificatation: build.query<CertificationType, void>({
      query: () => ({
        url: `reports/certification`,
      }),
    }),
    getLevelsCompletion: build.query<LevelCompletion[], void>({
      query: () => ({
        url: `reports/completion/levels`,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetReportAreasQuery,
  useGetReportsQuery,
  useGetDiversityMakeupReportsQuery,
  useGetReportByAreaQuery,
  useGetCompanyBreakdownQuery,
  useGetCompanyPayGapBreakdownQuery,
  useGetPracticesQuery,
  useGetCertificatationQuery,
  useGetLevelsCompletionQuery,
} = reportsApi
